import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { Loader, Center, Stack, Text } from '@mantine/core';
import { useIsMineMinerAdmin, useIsOrganizationMember } from '../../../application/store/authStore';
import { useAuth } from '../../hooks/useAuth';
import { PUBLIC_ROUTES, UTILITY_ROUTES } from '../../routing/routes';

/**
 * OrganizationRoute — route guard for the organization area (sprint-239, epic-039 Canary
 * increment A).
 *
 * Requires authentication AND one of the two organization roles. Mirrors `MineClientRoute`'s
 * loading/redirect contract so the areas behave identically at the edges; what differs is who
 * gets in.
 *
 * 🔴 THIS GUARD DECIDES WHICH SCREEN TO DRAW, NOT WHAT DATA MAY BE READ.
 *
 * It reads role names off the persisted login result. That is enough to answer "does this
 * person belong in the organization area", and it is nowhere near enough to answer "may they
 * see organization 1" — which is resolved server-side against a live OrganizationMembership
 * row on every request (DR-002). Passing this guard grants nothing.
 *
 * ⚠️ A user with an organization role but no memberships passes deliberately. They are a real,
 * expected state — invited but not yet attached — and bouncing them to /access-denied would
 * say "you are not allowed here" when the truth is "there is nothing here yet". The landing
 * page says the true thing instead.
 *
 * 🔴 A mine-client role does NOT admit anyone here, and an organization role does not admit
 * anyone to the client area. The two lanes are separate (DR-002 clause 2) and the guards say
 * so independently.
 */
export const OrganizationRoute: React.FC = (): React.ReactElement => {
  const { isAuthenticated, isLoading } = useAuth();
  const isOrganizationMember = useIsOrganizationMember();
  // sprint-267 (epic-039/DR-017): a MineMiner admin may VIEW any organization page. The server
  // gate is InternalAdminOrganizationViewHandler (safe methods only); this only stops the SPA
  // bouncing an admin to access-denied before the API is even asked.
  const isMineMinerAdmin = useIsMineMinerAdmin();

  if (isLoading) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            Loading...
          </Text>
        </Stack>
      </Center>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={PUBLIC_ROUTES.LOGIN} replace />;
  }

  if (!isOrganizationMember && !isMineMinerAdmin) {
    return <Navigate to={UTILITY_ROUTES.ACCESS_DENIED} replace />;
  }

  return <Outlet />;
};
